import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'motion/react';
import { useToast } from "../../hooks/useToast";
import { signup, authenticateWithGoogle } from '../../api/auth';
import { acceptGroupInvitation } from '../../api/group';
import Logo from '../logo/Logo';
import { Input } from '../common/Input';
import { Button } from '../common/Button';
import { ArrowRightIcon, PersonIcon, EnvelopeClosedIcon, LockClosedIcon } from '@radix-ui/react-icons';
import GoogleSignInButton from '../common/GoogleSignInButton';
import PasswordCriteria from '../common/PasswordCriteria';
import PrivacyLink from '../common/PrivacyLink';

const SignupBox = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const groupInvitationId = searchParams.get('groupInvitation');
  const { toast } = useToast();
  const [formData, setFormData] = useState({ name: '', email: '', password: '' });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    // If there's a group invitation, show a toast to inform the user
    if (groupInvitationId) {
      toast({
        title: "Group Invitation",
        description: "Complete signup to join the group you were invited to",
        type: "info"
      });
    }
  }, [groupInvitationId, toast]);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleGroupInvitation = async (groupId) => {
    try {
      await acceptGroupInvitation(groupId);
      toast({
        title: "Success",
        description: "You have joined the group successfully!",
        type: "success"
      });
      navigate(`/group/${groupId}/view`);
    } catch (error) {
      toast({
        title: "Warning",
        description: "Joined successfully, but couldn't accept group invitation automatically. You can try again later.",
        type: "warning"
      });
      navigate('/groups');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      await signup(formData.name, formData.email, formData.password);
      
      if (groupInvitationId) {
        // If this signup came from a group invitation, accept it
        await handleGroupInvitation(groupInvitationId);
      } else {
        navigate('/home');
      }
    } catch (error) {
      toast({
        title: "Error",
        description: error.message || 'Failed to signup',
        type: "error"
      });
      setLoading(false);
    }
  };

  const handleGoogleSuccess = async (credential) => {
    try {
      await authenticateWithGoogle(credential);
      
      if (groupInvitationId) {
        // If this signup came from a group invitation, accept it
        await handleGroupInvitation(groupInvitationId);
      } else {
        navigate('/home');
      }
    } catch (error) {
      toast({
        title: "Error",
        description: error.message || "Failed to sign in with Google",
        type: "error"
      });
    }
  };

  const handleGoogleError = (error) => {
    toast({
      title: "Error",
      description: error || "Failed to sign in with Google",
      type: "error"
    });
  };

  // Animation variants for staggered form fields
  const containerVariants = {
    hidden: { opacity: 0 },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, x: -20 },
    show: { opacity: 1, x: 0 }
  };

  return (
    <motion.div 
      className="min-h-screen grid lg:grid-cols-2 bg-gradient-to-br from-secondary/5 via-background to-primary/5"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5 }}
    >
      {/* Left side - Branding */}
      <div className="hidden lg:flex flex-col items-center justify-center p-8 relative overflow-hidden">
        <motion.div
          initial={{ scale: 0.8, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ delay: 0.2, duration: 0.5 }}
          className="relative z-10"
        >
          <div className="flex justify-center">
            <Logo />
          </div>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4, duration: 0.5 }}
            className="mt-6 space-y-4"
          >
            <p className="text-lg text-center text-muted-foreground max-w-sm">
              Join thousands of users who track their time and boost productivity
            </p>
            <div className="flex justify-center gap-8 mt-8">
              {['Track', 'Analyze', 'Reflect'].map((feature, i) => (
                <motion.div
                  key={feature}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.6 + (i * 0.1), duration: 0.5 }}
                  className="text-center"
                >
                  <div className="text-primary text-sm font-medium">{feature}</div>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </motion.div>
        
        {/* Background decoration */}
        <div className="absolute inset-0 z-0">
          <motion.div
            className="absolute inset-0 bg-secondary/5 backdrop-blur-3xl"
            // initial={{ opacity: 0, scale: 0.8 }}
            // animate={{ opacity: 1, scale: 1 }}
            // transition={{ delay: 0.3, duration: 0.8 }}
          />
          {[...Array(3)].map((_, i) => (
            <motion.div
              key={i}
              className="absolute rounded-full bg-gradient-to-br from-primary/10 to-secondary/10 backdrop-blur-3xl"
              style={{
                width: '30vw',
                height: '30vw',
                top: ['20%', '50%', '70%'][i],
                left: ['20%', '60%', '30%'][i],
              }}
              initial={{ opacity: 0, scale: 0 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.4 + (i * 0.2), duration: 1 }}
            />
          ))}
        </div>
      </div>

      {/* Right side - Signup form */}
      <div className="flex items-center justify-center p-8">
        <motion.div
          className="w-full max-w-md space-y-8"
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.4, duration: 0.5 }}
        >
          <div className="lg:hidden mb-8 flex justify-center">
            <Logo />
          </div>

          <div className="space-y-2 text-center">
            <h1 className="text-2xl font-semibold tracking-tight">Create an account</h1>
            <p className="text-sm text-muted-foreground">
              One step closer to a more productive you
            </p>
          </div>

          <motion.form
            variants={containerVariants}
            initial="hidden"
            animate="show"
            onSubmit={handleSubmit}
            className="space-y-4"
          >
            <motion.div variants={itemVariants} className="space-y-2">
              <div className="relative">
                <PersonIcon className="absolute left-3 top-3 h-4 w-4 text-muted-foreground z-10" />
                <Input
                  type="text"
                  name="name"
                  placeholder="Full Name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  className="w-full pl-10"
                  maxLength={200}
                />
              </div>
            </motion.div>

            <motion.div variants={itemVariants} className="space-y-2">
              <div className="relative">
                <EnvelopeClosedIcon className="absolute left-3 top-3 h-4 w-4 text-muted-foreground z-10" />
                <Input
                  type="email"
                  name="email"
                  placeholder="Email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="w-full pl-10"
                  maxLength={255}
                />
              </div>
            </motion.div>

            <motion.div variants={itemVariants} className="space-y-2">
              <div className="relative">
                <LockClosedIcon className="absolute left-3 top-3 h-4 w-4 text-muted-foreground z-10" />
                <Input
                  type="password"
                  name="password"
                  placeholder="Password"
                  value={formData.password}
                  onChange={handleChange}
                  required
                  className="w-full pl-10"
                  maxLength={20}
                />
              </div>
              <AnimatePresence>
                {formData.password && (
                  <div className="mt-2">
                    <PasswordCriteria password={formData.password} />
                  </div>
                )}
              </AnimatePresence>
            </motion.div>

            <Button
              type="submit"
              className="w-full button-glow"
              disabled={loading}
            >
              {loading ? (
                <motion.div
                  className="h-5 w-5 border-2 border-current border-r-transparent rounded-full"
                  animate={{ rotate: 360 }}
                  transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                />
              ) : (
                <>
                  Create Account
                  <ArrowRightIcon className="ml-2 h-4 w-4" />
                </>
              )}
            </Button>

            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <span className="w-full border-t" />
              </div>
              <div className="relative flex justify-center text-xs">
                <span className="bg-gradient-to-br from-primary/5 via-background to-secondary/5 px-2 text-muted-foreground">
                  or continue with
                </span>
              </div>
            </div>

            <GoogleSignInButton
              onSuccess={handleGoogleSuccess}
              onError={handleGoogleError}
            />
          </motion.form>

          <div className="text-center text-sm">
            <span className="text-muted-foreground">Already have an account? </span>
            <Link 
              to="/login" 
              className="font-medium text-primary hover:text-primary/90 transition-colors"
            >
              Sign in
            </Link>
          </div>

          <PrivacyLink />
        </motion.div>
      </div>
    </motion.div>
  );
};

export default SignupBox; 